import {
  CreateRideRequest,
  GetRideNearbyVehiclesV1Params,
  GetRideTariffsV1Params,
  RideDto,
  RideLocationsDto,
  RideTariffDto,
  RideVehicleDto,
  UpdateRideLocationRequest,
} from '@/services/platform/api/model';
import {
  createRideV1,
  listRidesV1,
  getRideV1,
  cancelRideV1,
  updateRideLocationV1,
  getRideLocationsV1,
  getRideTariffsV1,
  getRideNearbyVehiclesV1,
} from '../../../api/onde-operator-reservations/onde-operator-reservations';
import { isRideInProgress } from './rideStatus';

export * from './rideStatus';

/** Thrown by `createRide` on HTTP 409 — the customer already has a non-terminal ride. */
export class RideAlreadyInProgressError extends Error {
  constructor() {
    super('ride_already_in_progress');
    this.name = 'RideAlreadyInProgressError';
  }
}

/** Thrown by `createRide` on HTTP 503 — Onde itself is unreachable/unavailable. */
export class RideServiceUnavailableError extends Error {
  constructor() {
    super('onde_unavailable');
    this.name = 'RideServiceUnavailableError';
  }
}

/**
 * Creates a ride order with Onde for the given pickup/dropoff and persists it.
 *
 * The app should subscribe to `/hubs/rides` for real-time status/driver-location
 * updates once the ride is created.
 *
 * @param request - Ride payload: `pickup`/`dropoff` coordinates and addresses,
 *   optional `vehicleType`/`tariffId`, and optional `pickupTime` (RFC3339) to
 *   schedule a pre-booked ride instead of ASAP.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so any localized response content matches the user's language.
 * @returns `RideDto` on HTTP 201.
 * @returns `undefined` on HTTP 400 (bad/unparseable `pickupTime` or request), 401
 *   (unauthenticated), or network error.
 * @throws {RideAlreadyInProgressError} on HTTP 409 — the customer already has a
 *   ride under way; see `cancelInProgressRides` to clear it.
 * @throws {RideServiceUnavailableError} on HTTP 503 — Onde itself is unreachable.
 */
export async function createRide(request: CreateRideRequest, locale?: string): Promise<RideDto | undefined> {
  try {
    const response = await createRideV1(request, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });

    if (response.status === 201) {
      return response.data as RideDto;
    }

    if (response.status === 409) {
      throw new RideAlreadyInProgressError();
    }

    if (response.status === 503) {
      throw new RideServiceUnavailableError();
    }

    console.warn(`createRide: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    if (error instanceof RideAlreadyInProgressError || error instanceof RideServiceUnavailableError)
      throw error;
    console.warn('createRide: request failed:', error);
    return undefined;
  }
}

/**
 * Cancels every one of the current user's rides that is currently in progress
 * (i.e. blocking `createRide` with a 409).
 *
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so any localized response content matches the user's language.
 * @returns `true` if every in-progress ride was cancelled (or none were found), `false` if any cancellation failed.
 */
export async function cancelInProgressRides(locale?: string): Promise<boolean> {
  const rides = await listRides(locale);
  const inProgress = (rides ?? []).filter(ride => isRideInProgress(ride.status));
  const results = await Promise.all(inProgress.map(ride => cancelRide(ride.publicGuid, locale)));
  return results.every(Boolean);
}

/**
 * Fetches the current user's ride history, most recent first.
 *
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so any localized response content matches the user's language.
 * @returns Array of `RideDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated) or network error.
 */
export async function listRides(locale?: string): Promise<RideDto[] | undefined> {
  try {
    const response = await listRidesV1({
      headers: locale ? { 'Accept-Language': locale } : {},
    });

    if (response.status === 200) {
      return response.data as RideDto[];
    }

    console.warn(`listRides: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('listRides: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches the current state of a ride, refreshed from Onde first if not yet terminal.
 *
 * Prefer subscribing to `/hubs/rides` for live tracking; use this only for the
 * initial load of a ride's detail screen.
 *
 * @param publicGuid - The ride's public GUID.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so any localized response content matches the user's language.
 * @returns `RideDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated), 404 (not found or not owned
 *   by the current user), or network error.
 */
export async function getRide(publicGuid: string, locale?: string): Promise<RideDto | undefined> {
  try {
    const response = await getRideV1(publicGuid, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });

    if (response.status === 200) {
      return response.data as RideDto;
    }

    console.warn(`getRide: unexpected status ${response.status} for id "${publicGuid}"`);
    return undefined;
  } catch (error) {
    console.warn(`getRide: request failed for id "${publicGuid}":`, error);
    return undefined;
  }
}

/**
 * Cancels a ride via Onde (`CANCELLED_BY_DISPATCH`).
 *
 * @param publicGuid - The ride's public GUID.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so any localized response content matches the user's language.
 * @returns `true` on HTTP 204 (cancelled successfully).
 * @returns `false` on 401 (unauthenticated), 404 (not found), 409 (already in a
 *   terminal state), 503 (Onde unavailable), or network error.
 */
export async function cancelRide(publicGuid: string, locale?: string): Promise<boolean> {
  try {
    const response = await cancelRideV1(publicGuid, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });
    return response.status === 204;
  } catch (error) {
    console.warn(`cancelRide: request failed for id "${publicGuid}":`, error);
    return false;
  }
}

/**
 * Forwards the passenger's current location to Onde so the driver can find them.
 *
 * @param publicGuid - The ride's public GUID.
 * @param request - The passenger's current `lat`/`lng`, plus optional
 *   `accuracy`/`bearing`/`speed`.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so any localized response content matches the user's language.
 * @returns `true` on HTTP 204.
 * @returns `false` on 401 (unauthenticated), 404 (not found), 409 (ride no
 *   longer active), 503 (Onde unavailable), or network error.
 */
export async function updateRideLocation(
  publicGuid: string,
  request: UpdateRideLocationRequest,
  locale?: string
): Promise<boolean> {
  try {
    const response = await updateRideLocationV1(publicGuid, request, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });
    return response.status === 204;
  } catch (error) {
    console.warn(`updateRideLocation: request failed for id "${publicGuid}":`, error);
    return false;
  }
}

/**
 * Fetches everything the "Where from" / "Where to" picker needs in one call: the
 * customer's saved address book, the JNcQUOI venue catalogue, Fashion Clinic stores,
 * and recently used places from their own ride history.
 *
 * Every entry shares the same shape (name/city/address/coordinates/source). Notes
 * from the API:
 * - Address-book entries may have `lat`/`lng: null` when the address was never
 *   geocoded — callers must geocode those on selection before booking a ride
 *   with them (see `geocodeAddress` in `components/shared/booking/engine/flows/range-rover/map`).
 * - `venues`/`stores` come from the CMS venue catalogue (split on the
 *   `isFashionClinicStore` flag) and only include entries with valid CMS GPS
 *   coordinates.
 * - `recentTrips` is de-duplicated by coordinate and capped, most recent first.
 *
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so venue/store names and labels are returned in the correct language.
 * @returns `RideLocationsDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated), 404, or network error.
 */
export async function getRideLocations(locale?: string): Promise<RideLocationsDto | undefined> {
  try {
    const response = await getRideLocationsV1({
      headers: locale ? { 'Accept-Language': locale } : {},
    });

    if (response.status === 200) {
      return response.data as RideLocationsDto;
    }

    console.warn(`getRideLocations: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getRideLocations: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches estimated ride fares for a pickup and/or dropoff point.
 *
 * @param params - At least one of `originLat`/`originLng` or
 *   `destinationLat`/`destinationLng` is required. Pass `pickupTime` to check
 *   fares for a scheduled pickup instead of ASAP — only tariffs with
 *   `preBooking: true` are returned for a future `pickupTime`.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so tariff `name`s are returned in the correct language.
 * @returns Array of `RideTariffDto` on HTTP 200, one per available vehicle/tariff type.
 * @returns `undefined` on HTTP 400 (bad request), 401 (unauthenticated), 503, or network error.
 */
export async function getRideTariffs(
  params: GetRideTariffsV1Params,
  locale?: string
): Promise<RideTariffDto[] | undefined> {
  try {
    const response = await getRideTariffsV1(params, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });

    if (response.status === 200) {
      return response.data as RideTariffDto[];
    }

    console.warn(`getRideTariffs: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getRideTariffs: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches active vehicles currently near a given origin point.
 *
 * @param params - `originLat`/`originLng` (required), plus optional
 *   `vehicleType`/`numberOfSeats` filters.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so any localized response content matches the user's language.
 * @returns Array of `RideVehicleDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated), 503 (Onde unavailable), or network error.
 */
export async function getRideNearbyVehicles(
  params: GetRideNearbyVehiclesV1Params,
  locale?: string
): Promise<RideVehicleDto[] | undefined> {
  try {
    const response = await getRideNearbyVehiclesV1(params, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });

    if (response.status === 200) {
      return response.data as RideVehicleDto[];
    }

    console.warn(`getRideNearbyVehicles: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getRideNearbyVehicles: request failed:', error);
    return undefined;
  }
}
